import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { vendorsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, TrendingUp, TrendingDown, ArrowRightLeft, Crown, History, Package, Minus, Plus, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const UNITS = ['pc', 'sqft', 'sqm', 'ft', 'm', 'kg', 'ltr', 'set', 'roll', 'box', 'lot', 'other'];

export default function VendorCompare() {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [comparisons, setComparisons] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Price history dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyArticle, setHistoryArticle] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Quick Add Article dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addForm, setAddForm] = useState({
    vendor_id: '', part_number: '', name: '', description: '', unit: 'pc',
    current_price: '', lead_time: '', min_order_qty: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [compRes, vendorRes] = await Promise.all([
        vendorsAPI.compareArticles(),
        vendorsAPI.list(),
      ]);
      setComparisons(compRes.data.comparisons || []);
      setVendors(vendorRes.data);
    } catch (err) {
      console.error('Failed to load comparisons:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddArticle = async (e) => {
    e.preventDefault();
    if (!addForm.vendor_id) return;
    setAddLoading(true);
    try {
      await vendorsAPI.createArticle(addForm.vendor_id, addForm);
      setAddOpen(false);
      setAddForm({ vendor_id: '', part_number: '', name: '', description: '', unit: 'pc', current_price: '', lead_time: '', min_order_qty: '' });
      fetchData(); // Refresh comparison
    } catch (err) { console.error(err); } finally { setAddLoading(false); }
  };

  const openPriceHistory = async (articleId, vendorName) => {
    setHistoryArticle({ articleId, vendorName });
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await vendorsAPI.getPriceHistory(articleId);
      setHistoryData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatCurrency = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN')}` : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const filtered = comparisons.filter(c =>
    c.article_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.group_key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Collect all unique vendor names across all comparisons for column headers
  const allVendors = [];
  const vendorSet = new Set();
  for (const comp of comparisons) {
    for (const v of comp.vendors) {
      if (!vendorSet.has(v.vendor_id)) {
        vendorSet.add(v.vendor_id);
        allVendors.push({ vendor_id: v.vendor_id, vendor_name: v.vendor_name });
      }
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted w-1/3 rounded" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-compare-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
              <ArrowRightLeft className="h-7 w-7 text-muted-foreground" />
              Vendor Comparison
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Compare prices across vendors for the same articles
            </p>
          </div>
          <div className="flex gap-3 items-center">
            {canEdit && (
              <Button onClick={() => setAddOpen(true)} data-testid="quick-add-article-btn">
                <Plus className="h-4 w-4 mr-2" />Add Article
              </Button>
            )}
            <div className="flex gap-3 text-center">
            <Card>
              <CardContent className="p-3">
                <p className="text-2xl font-light">{comparisons.length}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Articles</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-2xl font-light">{allVendors.length}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendors</p>
              </CardContent>
            </Card>
          </div>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by article name or part number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="compare-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Comparison Table */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <ArrowRightLeft className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-medium mb-2">No articles to compare</h3>
              <p className="text-sm text-muted-foreground">
                {comparisons.length === 0
                  ? 'Add articles to your vendors to start comparing prices'
                  : 'No articles match your search'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filtered.map((comp) => {
              const vendorPrices = comp.vendors.map(v => v.current_price);
              const lowestCurrent = Math.min(...vendorPrices);
              const highestCurrent = Math.max(...vendorPrices);
              const allLowest = Math.min(...comp.vendors.map(v => v.lowest_price));
              const hasMultipleVendors = comp.vendors.length > 1;
              const savings = hasMultipleVendors ? highestCurrent - lowestCurrent : 0;

              return (
                <Card key={comp.group_key} data-testid={`compare-group-${comp.group_key}`} className="overflow-hidden">
                  <CardContent className="p-0">
                    {/* Article header */}
                    <div className="px-4 py-3 bg-muted/30 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <h3 className="font-medium">{comp.article_name}</h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {comp.match_type === 'part_number' && (
                              <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{comp.group_key}</span>
                            )}
                            <span>{comp.vendors.length} vendor{comp.vendors.length > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      {savings > 0 && (
                        <div className="flex items-center gap-1.5 text-xs px-2.5 py-1 bg-green-50 text-green-700 rounded-full border border-green-200">
                          <TrendingDown className="h-3 w-3" />
                          Save {formatCurrency(savings)} by choosing cheapest
                        </div>
                      )}
                    </div>

                    {/* Vendor comparison grid */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs w-44">Vendor</TableHead>
                            <TableHead className="text-xs text-right">Current Price</TableHead>
                            <TableHead className="text-xs text-right">Lowest Ever</TableHead>
                            <TableHead className="text-xs text-right">Highest</TableHead>
                            <TableHead className="text-xs text-center">Price Changes</TableHead>
                            <TableHead className="text-xs">Lead Time</TableHead>
                            <TableHead className="text-xs text-center">Min Qty</TableHead>
                            <TableHead className="text-xs text-right">History</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comp.vendors
                            .sort((a, b) => a.current_price - b.current_price)
                            .map((v, idx) => {
                              const isCheapest = hasMultipleVendors && v.current_price === lowestCurrent;
                              const isMostExpensive = hasMultipleVendors && v.current_price === highestCurrent && highestCurrent !== lowestCurrent;
                              const isHistoricalBest = v.lowest_price === allLowest && hasMultipleVendors;

                              return (
                                <TableRow
                                  key={v.vendor_id}
                                  className={isCheapest ? 'bg-green-50/50' : isMostExpensive ? 'bg-red-50/30' : ''}
                                  data-testid={`compare-vendor-${v.vendor_id}`}
                                >
                                  <TableCell>
                                    <Link
                                      to={`/vendors/${v.vendor_id}`}
                                      className="flex items-center gap-2 hover:text-primary transition-colors"
                                    >
                                      {isCheapest && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                                      <span className="font-medium text-sm">{v.vendor_name}</span>
                                    </Link>
                                    {isCheapest && (
                                      <span className="text-[10px] text-green-600 font-medium ml-5">BEST PRICE</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <span className={`font-semibold ${isCheapest ? 'text-green-700' : isMostExpensive ? 'text-red-600' : ''}`}>
                                      {formatCurrency(v.current_price)}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <span className={`text-sm ${isHistoricalBest ? 'text-green-600 font-medium' : 'text-muted-foreground'}`}>
                                      {formatCurrency(v.lowest_price)}
                                      {isHistoricalBest && <TrendingDown className="h-3 w-3 inline ml-1" />}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right text-sm text-muted-foreground">
                                    {formatCurrency(v.highest_price)}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <span className="text-sm text-muted-foreground">{v.price_entries}</span>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {v.lead_time || <Minus className="h-3 w-3" />}
                                  </TableCell>
                                  <TableCell className="text-center text-sm text-muted-foreground">
                                    {v.min_order_qty || <Minus className="h-3 w-3 mx-auto" />}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => openPriceHistory(v.article_id, v.vendor_name)}
                                      data-testid={`view-history-${v.article_id}`}
                                    >
                                      <History className="h-3.5 w-3.5" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Price History Dialog */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />Price History
              </DialogTitle>
              <DialogDescription>{historyArticle?.vendorName}</DialogDescription>
            </DialogHeader>
            <div className="max-h-64 overflow-y-auto border rounded-md">
              {historyLoading ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
              ) : historyData.length === 0 ? (
                <p className="p-4 text-sm text-center text-muted-foreground">No history</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs text-right">Price</TableHead>
                      <TableHead className="text-xs">Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyData.map(h => (
                      <TableRow key={h.history_id}>
                        <TableCell className="text-xs">{formatDate(h.recorded_at)}</TableCell>
                        <TableCell className="text-right font-medium text-sm">{formatCurrency(h.price)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{h.note || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Add Article Dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Article to Vendor</DialogTitle>
              <DialogDescription>Add a product to a vendor's catalog for comparison</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddArticle}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <Select value={addForm.vendor_id || 'none'} onValueChange={v => setAddForm({ ...addForm, vendor_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="quick-add-vendor-select"><SelectValue placeholder="Select a vendor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a vendor</SelectItem>
                      {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Part Number</Label><Input value={addForm.part_number} onChange={e => setAddForm({ ...addForm, part_number: e.target.value })} placeholder="e.g., FLR-001" /></div>
                  <div className="space-y-2"><Label>Name</Label><Input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} required placeholder="e.g., Italian Marble" data-testid="quick-add-name" /></div>
                </div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} rows={2} placeholder="Specs, dimensions..." /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Unit</Label>
                    <Select value={addForm.unit} onValueChange={v => setAddForm({ ...addForm, unit: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Price (₹)</Label><Input type="number" step="0.01" value={addForm.current_price} onChange={e => setAddForm({ ...addForm, current_price: e.target.value })} required data-testid="quick-add-price" /></div>
                  <div className="space-y-2"><Label>Lead Time</Label><Input value={addForm.lead_time} onChange={e => setAddForm({ ...addForm, lead_time: e.target.value })} placeholder="e.g., 2 weeks" /></div>
                </div>
                <div className="space-y-2"><Label>Min Order Qty</Label><Input type="number" value={addForm.min_order_qty} onChange={e => setAddForm({ ...addForm, min_order_qty: e.target.value })} placeholder="Optional" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={addLoading || !addForm.vendor_id} data-testid="quick-add-submit">
                  {addLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Add Article
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
